import { autoinject, bindable } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { GridOptions, CellValueChangedEvent } from "ag-grid";
import { HttpClientService } from "../../services/http-client-service";
import { DialogFactory } from "../../services/dialog-factory";
import { AuthService } from "../../services/auth";

@autoinject
export class Translations {
  @bindable() languages: { id: string; text: string }[];
  @bindable() sourceLanguage: string;
  @bindable() targetLanguage: string;
  @bindable() changed: boolean = false;
  @bindable() errorMessage?: string;
  @bindable() editable: boolean = false;

  gridOptions: GridOptions = {
    enableSorting: true,
    enableFilter: true,
    enableColResize: true,
    onCellValueChanged: this.onCellValueChanged,
    context: {
      // remember this view model, for later use in callbacks
      viewModel: this,
    },
  };

  constructor(
    private i18n: I18N,
    private dialogFactory: DialogFactory,
    private httpClientService: HttpClientService,
    private authService: AuthService,
  ) {
    this.editable = authService.hasRight("admin-write");
    let languages = this.i18n.i18next.t("language", { returnObjects: true });
    this.languages = Object.keys(languages).map((l) => {
      return {
        id: l,
        text: languages[l],
      };
    });
    this.sourceLanguage = this.languages[0].id;
    this.targetLanguage = this.languages[0].id;
  }

  async onGridReady() {
    if (!this.gridOptions.api) return;
    const r = await this.httpClientService
      .get()
      .get(
        `admin/translations?s=${this.sourceLanguage}&t=${this.targetLanguage}`,
      );
    this.gridOptions.api!.setRowData(r.content.result);
    this.gridOptions.api!.sizeColumnsToFit();
  }

  onGridSizeChanged() {
    this.gridOptions.api!.sizeColumnsToFit();
  }

  async save() {
    this.errorMessage = undefined;
    let updatedRows = this.getUpdatedRows();
    try {
      await this.httpClientService
        .get()
        .post(
          `admin/translations/update?s=${this.sourceLanguage}&t=${this.targetLanguage}`,
          updatedRows,
        );
      await this.reload();
      this.errorMessage = this.i18n.tr(
        "admin.translations.actions.savedSuccessfully",
      );
    } catch (e) {
      console.error(e);
      this.errorMessage =
        "unexpected error: " +
        e.statusCode +
        " " +
        e.statusText +
        (e.response ? ": " + e.response : "");
    }
  }

  private getUpdatedRows() {
    let updatedRows: any[] = [];
    this.gridOptions.api!.getModel().forEachNode((row) => {
      if (row.data.$status == "changed") updatedRows.push(row.data);
    });
    return updatedRows;
  }

  async revert() {
    await this.reload();
  }

  async reload() {
    this.changed = false;
    this.errorMessage = undefined;
    await this.onGridReady();
  }

  async askReload() {
    if (!this.changed) return this.reload();
    else {
      const message = this.i18n.tr("admin.translations.action.reallyReload");
      const confirmed = await this.dialogFactory.openOkCancel(message);
      if (confirmed) {
        return this.reload();
      }
    }
  }

  private onCellValueChanged(event: CellValueChangedEvent) {
    console.log(event);
    let row = event.data;
    // note: we cannot access 'this' here; 'this' is a callback function
    // but luckily we put the viewModel into our custom 'context' object
    let self = event.context.viewModel;
    if (self.sourceLanguage === self.targetLanguage) {
      // Source and target language are the same: copy left column value
      // to right column value and vice versa.
      // First we need to find out the name of the field that has changed.
      // This is available in params.colDef, but this field is not part
      // of the typescript definition. We access it nevertheless:
      let changedField = event.colDef.field;
      if (changedField === "s")
        row.t = row.s; // copy source to target
      else if (changedField === "t") row.s = row.t; // copy target to source
    }
    // mark row as changed
    row.$status = "changed";
    event.api.redrawRows(); // needed to redraw the 'key' column, too (to make it blue)
    // mark the form as changed
    self.changed = true;
  }

  download() {
    this.gridOptions.api!.exportDataAsCsv({
      columnSeparator: ";",
      fileName:
        "Pumpselection_Uebersetzungen_" +
        this.sourceLanguage +
        "-" +
        this.targetLanguage +
        ".csv",
    });
  }
}
